/**
 * Domain catalog — the authoritative registry of domains a school can run.
 *
 * `schools/{schoolId}/domains` is the per-school instantiation; THIS module is
 * where "what domains exist in Swan" is declared. The setup wizard, upsell
 * surfaces, and any future domain marketplace render from here — never from a
 * hardcoded list in a component or store.
 *
 * Every entry resolves to a centrally versioned Prototype. `kind: protected`
 * remains only as a compatibility discriminator for the established Behaviour
 * and Recognition runtimes; it is not a governance or editability boundary.
 *
 * Every entry is validated at import time: governed specs run through
 * `assertDomainShape`, protected refs resolve, and a launch entry must resolve
 * to one versioned central definition.
 */

import { assertDomainShape } from './schemaAssert.js';
import {
  CENTRAL_GOVERNED_DOMAIN_CONFIGS,
  PROTECTED_DOMAIN_DEFINITIONS,
} from './protectedDefinitions.js';

const CATEGORIES = ['core', 'classroom', 'wellbeing', 'academics', 'operations'];
const TIERS = ['free', 'pro'];
const AVAILABILITIES = ['launch', 'coming_soon'];
const KINDS = ['protected', 'governed'];

/**
 * Feature keys (see src/features/subscription/features.js) gating parts of the
 * catalog. `featureKeyFor(entry)` resolves per-entry; custom domain creation
 * (free-text, not a catalog entry) is gated by CUSTOM_DOMAIN_FEATURE_KEY.
 */
export const ADVANCED_DOMAINS_FEATURE_KEY = 'advancedDomains';
export const CUSTOM_DOMAIN_FEATURE_KEY = 'customDomains';

/**
 * Validate one catalog entry at import time, then freeze it. A malformed entry
 * throws synchronously — dev server crash, build fail, test-load fail — the
 * same safety net assertDomainShape gives the protected configs.
 */
function defineEntry(entry) {
  const where = `[domain catalog] ${entry?.id || '<no id>'}`;
  if (!entry?.id || typeof entry.id !== 'string') throw new Error(`${where}: missing string id`);
  if (!entry.label || typeof entry.label !== 'string') throw new Error(`${where}: missing string label`);
  if (typeof entry.description !== 'string') throw new Error(`${where}: missing description`);
  if (!CATEGORIES.includes(entry.category)) throw new Error(`${where}: bad category "${entry.category}"`);
  if (!TIERS.includes(entry.tier)) throw new Error(`${where}: bad tier "${entry.tier}"`);
  if (!AVAILABILITIES.includes(entry.availability)) throw new Error(`${where}: bad availability "${entry.availability}"`);
  if (!KINDS.includes(entry.kind)) throw new Error(`${where}: bad kind "${entry.kind}"`);

  if (entry.kind === 'protected') {
    if (!PROTECTED_DOMAIN_DEFINITIONS[entry.protectedId]) {
      throw new Error(`${where}: protectedId "${entry.protectedId}" has no canonical definition`);
    }
    if (entry.spec) throw new Error(`${where}: protected entries must not carry a spec`);
  }

  if (entry.kind === 'governed') {
    if (entry.alwaysOn) throw new Error(`${where}: governed entries cannot be alwaysOn`);
    if (entry.availability === 'launch') {
      const managed = CENTRAL_GOVERNED_DOMAIN_CONFIGS[entry.definitionId];
      if (!managed && !entry.spec) {
        throw new Error(`${where}: launch governed entry needs definitionId or spec`);
      }
      if (managed && entry.spec) {
        throw new Error(`${where}: governed definition refs must not copy a spec`);
      }
      if (entry.spec) {
        const specName = entry.spec.name;
        if (PROTECTED_DOMAIN_DEFINITIONS[specName]) {
          throw new Error(`${where}: spec name "${specName}" collides with a protected Domain id`);
        }
        assertDomainShape({
          name: specName,
          primary: entry.spec.primary,
          followUp: entry.spec.followUp || [],
        });
      }
    } else if (entry.spec) {
      throw new Error(`${where}: coming_soon entries are display-only — no spec`);
    }
  }

  return Object.freeze({
    ...entry,
    prototypeId: entry.prototypeId
      || `swan.${entry.definitionId || entry.protectedId || entry.id}`,
  });
}

/** Full terminology block for a creatable spec — createDomain takes it whole. */
function terminology(event, plural, categoryLabel = 'category', categoryPlural = 'categories') {
  return {
    event: { label: event, plural, description: '' },
    category: { label: categoryLabel, plural: categoryPlural, description: '' },
    type: { label: 'type', plural: 'types', description: '' },
    action: { label: 'action', plural: 'actions', description: '' },
    rule: { label: 'rule', plural: 'rules', description: '' },
    queue: { label: 'queue', plural: 'queues', description: '' },
  };
}

export const DOMAIN_CATALOG = Object.freeze([
  // ── Core — always on, seeded for every school ─────────────────────────────
  defineEntry({
    id: 'discipline',
    label: 'Behaviour',
    description: 'Behaviour incidents, consequences, and the consequence queue.',
    icon: '⚖️',
    category: 'core',
    tier: 'free',
    availability: 'launch',
    wizard: true,
    kind: 'protected',
    protectedId: 'discipline',
    alwaysOn: true,
  }),
  defineEntry({
    id: 'recognition',
    label: 'Recognition',
    description: 'Positive recognition aligned with your school values — feeds merits, tiers, and Stories.',
    icon: '🌟',
    category: 'core',
    tier: 'free',
    availability: 'launch',
    wizard: true,
    kind: 'protected',
    protectedId: 'recognition',
    alwaysOn: true,
  }),
  defineEntry({
    id: 'incident-reports',
    label: 'Incident Reports',
    description: 'Full staff incident reports for Behaviour cases.',
    icon: '📋',
    category: 'core',
    tier: 'free',
    availability: 'launch',
    wizard: false,
    kind: 'governed',
    definitionId: 'incident-reports',
  }),

  // ── Classroom — free picks ────────────────────────────────────────────────
  defineEntry({
    id: 'attendance',
    label: 'Attendance',
    description: 'Daily and period registers with reasons, excuses, and supporting documents.',
    icon: '📋',
    category: 'classroom',
    tier: 'free',
    availability: 'launch',
    wizard: true,
    kind: 'governed',
    definitionId: 'attendance',
    defaultSelected: true,
  }),
  defineEntry({
    id: 'participation',
    label: 'Participation',
    description: 'Quick in-lesson engagement tracking — who contributed, who needs a nudge.',
    icon: '🙋',
    category: 'classroom',
    tier: 'free',
    availability: 'launch',
    wizard: true,
    kind: 'governed',
    definitionId: 'participation',
    defaultSelected: true,
  }),
  // Attendance sub-registers are centrally governed, but represented in the
  // wizard by the single Attendance card — never shown as separate picks.
  defineEntry({
    id: 'daily-register',
    label: 'Daily register',
    description: 'The once-a-day roll call register.',
    icon: '📋',
    category: 'classroom',
    tier: 'free',
    availability: 'launch',
    wizard: false,
    kind: 'governed',
    definitionId: 'daily-register',
  }),
  defineEntry({
    id: 'period-register',
    label: 'Period register',
    description: 'Per-period attendance for subject lessons.',
    icon: '📋',
    category: 'classroom',
    tier: 'free',
    availability: 'launch',
    wizard: false,
    kind: 'governed',
    definitionId: 'period-register',
  }),
  defineEntry({
    id: 'homework',
    label: 'Homework',
    description: 'Track homework completion per lesson — complete, partial, or missing.',
    icon: '📓',
    category: 'classroom',
    tier: 'free',
    availability: 'launch',
    wizard: true,
    kind: 'governed',
    spec: {
      name: 'homework',
      label: 'Homework',
      description: 'Homework completion tracking',
      color: 'primary',
      icon: '📓',
      defaultView: 'list',
      primary: {
        id: 'status',
        label: 'Status',
        type: 'multiChoice',
        options: [
          { value: 'complete', label: 'Complete', color: 'success' },
          { value: 'partial', label: 'Partial', color: 'warning' },
          { value: 'missing', label: 'Not done', color: 'error' },
        ],
      },
      followUp: [
        { id: 'comment', label: 'Comment', type: 'text', optional: true },
      ],
      terminology: terminology('homework check', 'homework checks', 'subject', 'subjects'),
    },
  }),
  defineEntry({
    id: 'late-arrivals',
    label: 'Late arrivals',
    description: 'Log who arrives late, why, and how late — separate from the full register.',
    icon: '⏰',
    category: 'classroom',
    tier: 'free',
    availability: 'launch',
    wizard: true,
    kind: 'governed',
    spec: {
      name: 'late-arrivals',
      label: 'Late arrivals',
      description: 'Late-arrival tracking',
      color: 'warning',
      icon: '⏰',
      defaultView: 'list',
      primary: {
        id: 'reason',
        label: 'Reason',
        type: 'multiChoice',
        options: [
          { value: 'transport', label: 'Transport', color: 'info' },
          { value: 'overslept', label: 'Overslept', color: 'warning' },
          { value: 'medical', label: 'Medical', color: 'success' },
          { value: 'other', label: 'Other', color: 'neutral' },
        ],
      },
      followUp: [
        { id: 'minutesLate', label: 'Minutes late', type: 'number', optional: true },
        { id: 'comment', label: 'Comment', type: 'text', optional: true },
      ],
      terminology: terminology('late arrival', 'late arrivals', 'reason', 'reasons'),
    },
  }),
  defineEntry({
    id: 'library',
    label: 'Library',
    description: 'Book loans, returns, and overdues per learner.',
    icon: '📖',
    category: 'classroom',
    tier: 'free',
    availability: 'launch',
    wizard: true,
    kind: 'governed',
    spec: {
      name: 'library',
      label: 'Library',
      description: 'Library loans and returns',
      color: 'info',
      icon: '📖',
      defaultView: 'list',
      primary: {
        id: 'action',
        label: 'Action',
        type: 'multiChoice',
        options: [
          { value: 'checked-out', label: 'Checked out', color: 'info' },
          { value: 'returned', label: 'Returned', color: 'success' },
          { value: 'overdue', label: 'Overdue', color: 'warning' },
          { value: 'lost-damaged', label: 'Lost / damaged', color: 'error' },
        ],
      },
      followUp: [
        { id: 'bookTitle', label: 'Book title', type: 'string', optional: true },
        { id: 'comment', label: 'Comment', type: 'text', optional: true },
      ],
      terminology: terminology('library entry', 'library entries'),
    },
  }),
  defineEntry({
    id: 'sports',
    label: 'Sports',
    description: 'Practice attendance, matches played, and team commitments.',
    icon: '🏅',
    category: 'classroom',
    tier: 'free',
    availability: 'launch',
    wizard: true,
    kind: 'governed',
    spec: {
      name: 'sports',
      label: 'Sports',
      description: 'Sports participation tracking',
      color: 'accent',
      icon: '🏅',
      defaultView: 'list',
      primary: {
        id: 'status',
        label: 'Status',
        type: 'multiChoice',
        options: [
          { value: 'practice', label: 'Attended practice', color: 'success' },
          { value: 'match', label: 'Played match', color: 'info' },
          { value: 'missed', label: 'Missed practice', color: 'warning' },
        ],
      },
      followUp: [
        { id: 'activity', label: 'Sport / team', type: 'string', optional: true },
        { id: 'comment', label: 'Comment', type: 'text', optional: true },
      ],
      terminology: terminology('sports entry', 'sports entries', 'sport', 'sports'),
    },
  }),
  defineEntry({
    id: 'uniform',
    label: 'Uniform',
    description: 'Uniform checks — correct, minor issue, or repeated infringement.',
    icon: '👔',
    category: 'classroom',
    tier: 'free',
    availability: 'launch',
    wizard: true,
    kind: 'governed',
    spec: {
      name: 'uniform',
      label: 'Uniform',
      description: 'Uniform compliance checks',
      color: 'secondary',
      icon: '👔',
      defaultView: 'list',
      primary: {
        id: 'status',
        label: 'Status',
        type: 'multiChoice',
        options: [
          { value: 'correct', label: 'Correct', color: 'success' },
          { value: 'minor', label: 'Minor issue', color: 'warning' },
          { value: 'serious', label: 'Serious issue', color: 'error' },
        ],
      },
      followUp: [
        { id: 'item', label: 'Item', type: 'string', optional: true },
        { id: 'comment', label: 'Comment', type: 'text', optional: true },
      ],
      terminology: terminology('uniform check', 'uniform checks'),
    },
  }),

  // ── Pro — the upgrade teasers ─────────────────────────────────────────────
  defineEntry({
    id: 'health',
    label: 'Health',
    description: 'Incident register with follow-ups — injuries, illness, referrals, parent notification.',
    icon: '🩹',
    category: 'wellbeing',
    tier: 'pro',
    availability: 'launch',
    wizard: true,
    kind: 'governed',
    definitionId: 'health',
  }),
  defineEntry({
    id: 'academics',
    label: 'Academics',
    description: 'Marks, assessments, and the gradebook — one mark sheet feeding every output.',
    icon: '📚',
    category: 'academics',
    tier: 'pro',
    availability: 'launch',
    wizard: true,
    kind: 'governed',
    definitionId: 'academics',
  }),

  defineEntry({
    id: 'policies',
    label: 'Policies',
    description: 'School policy library — versioned PDFs, governing-body approval, parent and staff signing.',
    icon: '📜',
    category: 'operations',
    tier: 'pro',
    availability: 'launch',
    wizard: true,
    kind: 'governed',
    definitionId: 'policies',
  }),

  // ── Coming soon — roadmap cards, display only ─────────────────────────────
  defineEntry({
    id: 'finance',
    label: 'Fees & finance',
    description: 'Fee tracking and payment follow-ups.',
    icon: '💳',
    category: 'operations',
    tier: 'pro',
    availability: 'coming_soon',
    wizard: true,
    kind: 'governed',
  }),
  defineEntry({
    id: 'admissions',
    label: 'Admissions',
    description: 'Applications, waiting lists, and enrolment pipelines.',
    icon: '🎓',
    category: 'operations',
    tier: 'pro',
    availability: 'coming_soon',
    wizard: true,
    kind: 'governed',
  }),
  defineEntry({
    id: 'co-curricular',
    label: 'Co-curricular',
    description: 'Clubs, societies, and cultural activities.',
    icon: '🎭',
    category: 'classroom',
    tier: 'free',
    availability: 'coming_soon',
    wizard: true,
    kind: 'governed',
  }),
  defineEntry({
    id: 'learning-support',
    label: 'Learning support',
    description: 'Interventions, accommodations, and support plans.',
    icon: '🧩',
    category: 'wellbeing',
    tier: 'pro',
    availability: 'coming_soon',
    wizard: true,
    kind: 'governed',
  }),
  defineEntry({
    id: 'staff-hr',
    label: 'Staff & HR',
    description: 'Staff leave, development, and reviews.',
    icon: '🗂️',
    category: 'operations',
    tier: 'pro',
    availability: 'coming_soon',
    wizard: true,
    kind: 'governed',
  }),
]);

// Catalog-level invariants — throw at import so a bad edit can't ship.
{
  const ids = DOMAIN_CATALOG.map((e) => e.id);
  const dupes = ids.filter((id, i) => ids.indexOf(id) !== i);
  if (dupes.length) throw new Error(`[domain catalog] duplicate ids: ${dupes.join(', ')}`);
}

/** Entries the setup wizard renders as cards, in catalog order. */
export function getWizardCatalog() {
  return DOMAIN_CATALOG.filter((e) => e.wizard);
}

/** Look an entry up by id; undefined when not in the catalog. */
export function getEntry(id) {
  return DOMAIN_CATALOG.find((e) => e.id === id);
}

/**
 * The subscription feature key gating this entry, or null when it's free.
 * Consumers pair this with useCanUse — when monetization is off, useCanUse
 * returns true and no padlock renders.
 */
export function featureKeyFor(entry) {
  return entry?.tier === 'pro' ? ADVANCED_DOMAINS_FEATURE_KEY : null;
}

export default DOMAIN_CATALOG;
